<template>
  <div class="upload-common-container" :style="containerStyle">
    <!-- 已上传的文件 -->
    <div v-if="fileUrl" class="file-preview">
      <!-- 图片预览 -->
      <div v-if="isImageFile(fileUrl)" class="image-preview">
        <el-image
          :src="fileUrl"
          class="uploaded-image"
          fit="cover"
          :preview-src-list="[fileUrl]"
        />
        <div class="image-actions">
          <i class="el-icon-view" @click="previewImage"></i>
          <i class="el-icon-delete" @click="removeFile"></i>
        </div>
      </div>
      
      <!-- 视频预览 -->
      <div v-else-if="isVideoFile(fileUrl)" class="video-preview">
        <div class="video-thumbnail">
          <video
            :src="fileUrl"
            preload="metadata"
            muted
            class="video-preview-element"
          >
            您的浏览器不支持视频播放
          </video>
          <div class="video-play-overlay">
            <i class="el-icon-video-play"></i>
          </div>
        </div>
        <div class="video-actions">
          <i class="el-icon-view" @click="previewVideo"></i>
          <i class="el-icon-delete" @click="removeFile"></i>
        </div>
      </div>
      
      <!-- 其他文件 -->
      <div v-else class="other-file-preview">
        <div class="file-content">
          <div class="file-icon">
            <i class="el-icon-document"></i>
            <div class="file-extension">{{ getFileExtension(fileUrl) }}</div>
          </div>
          <a :href="fileUrl" target="_blank" class="file-name">{{ getFileNameFromUrl(fileUrl) }}</a>
        </div>
        <div class="file-actions">
          <i class="el-icon-view" @click="previewFile"></i>
          <i class="el-icon-delete" @click="removeFile"></i>
        </div>
      </div>
    </div>
    
    <!-- 上传按钮 -->
    <div 
      v-else
      v-loading="uploading"
      class="upload-trigger"
      :class="sizeClass"
      @click="triggerUpload"
    >
      <i class="el-icon-plus upload-icon"></i>
      <div class="upload-text">{{ uploadText }}</div>
      <div class="upload-tip" v-if="showTip">
        支持常见图片、视频及其他文件格式
      </div>
    </div>
    
    <!-- 隐藏的文件输入框 -->
    <input 
      type="file" 
      ref="fileInput" 
      @change="handleFileChange" 
      :accept="accept"
      style="display: none;"
    />
  </div>
</template>

<script>
export default {
  name: 'UploadCommon',
  props: {
    value: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    // 文件大小限制(MB)
    maxSize: {
      type: Number,
      default: 100
    },
    // 自定义接受的文件类型
    accept: {
      type: String,
      default: '*'
    },
    width: {
      type: String,
      default: '320px'
    },
    height: {
      type: String,
      default: '200px'
    },
    uploadText: {
      type: String,
      default: '点击上传'
    },
    showTip: {
      type: Boolean,
      default: true
    },
    // 新增size属性，只支持数字
    size: {
      type: Number,
      default: null
    }
    //   type: Boolean,
    //   default: false
    // }
  },
  data() {
    return {
      uploading: false,
      fileUrl: this.value
    };
  },
  computed: {
    // 计算容器样式
    containerStyle() {
      if (this.size) {
        return {
          width: this.size + 'px',
          height: this.size + 'px',
          // 添加最小尺寸限制，确保内容可显示
          minWidth: '40px',
          minHeight: '40px'
        };
      }
      return {
        width: this.width,
        height: this.height
      };
    },
    // 根据simple计算样式类
    sizeClass() {
      //   return 'size-simple';
      // }
      return '';
    }
  },
  watch: {
    value(newVal) {
      this.fileUrl = newVal;
    },
    fileUrl(newVal) {
      this.$emit('input', newVal);
    }
  },
  methods: {
    // 触发文件选择
    triggerUpload() {
      if (this.disabled || this.uploading) return;
      this.$refs.fileInput.click();
    },
    
    // 处理文件选择
    async handleFileChange(event) {
      const file = event.target.files[0];
      if (!file) return;
      
      // 校验大小
      if (file.size / 1024 / 1024 > this.maxSize) {
        this.$message.error(`文件大小不能超过 ${this.maxSize}MB!`);
        this.$refs.fileInput.value = '';
        return;
      }
      
      try {
        await this.uploadFile(file);
      } catch (error) {
        this.$message.error('上传失败，请重试');
      } finally {
        this.$refs.fileInput.value = '';
      }
    },
    
    // 上传文件
    async uploadFile(file) {
      this.uploading = true;
      try {
        const fileName = this.generateFileName(file.name);
        const fileUrl = await this.uploadS3File(file, fileName);
        this.fileUrl = fileUrl;
        this.$message.success('文件上传成功');
      } catch (error) {
        this.$message.error('文件上传失败');
      } finally {
        this.uploading = false;
      }
    },
    
    // S3上传
    async uploadS3File(file, fileName) {
      const { data: resData } = await this.$get('s3/config/info', {});
      if (resData.result !== 200) throw new Error('获取S3配置失败');
      const s3Config = resData.data;
      const accountid = "05e3a3acf3a5440a0f6aa4e94818fac2";
      const accessKeyId = "adfaeaebf3a0adc777a2365aa4f7500a";
      const accessKey = "93f858b5e3e7d01f34dc25fcd0e228c61124872996059c2ec464a0431e0355b4";
      const s3 = new AWS.S3({
        params: { Bucket: "static-taotan" },
        endpoint: `https://${accountid}.r2.cloudflarestorage.com`,
        accessKeyId: `${accessKeyId}`,
        secretAccessKey: `${accessKey}`,
        signatureVersion: "v4",
      });
      const albumPhotosKey = "micros/";
      const photoKey = albumPhotosKey + fileName;
      return new Promise((resolve, reject) => {
        s3.upload(
          {
            Key: photoKey,
            Body: file,
          },
          (error, data) => {
            if (error) {
              reject(error);
            } else {
              const domain = s3Config.domain;
              const fileUrl = `${domain}/${data.key}`;
              resolve(fileUrl);
            }
          }
        );
      });
    },
    
    // 生成文件名
    generateFileName(originalName) {
      const data = new Date();
      const year = data.getFullYear().toString();
      const month = (data.getMonth() + 1).toString().padStart(2, '0');
      const day = data.getDate().toString().padStart(2, '0');
      const hours = data.getHours().toString().padStart(2, '0');
      const minutes = data.getMinutes().toString().padStart(2, '0');
      const seconds = data.getSeconds().toString().padStart(2, '0');
      const ms = data.getMilliseconds().toString().padStart(3, '0');
      return `${year}${month}${day}${hours}${minutes}${seconds}${ms}.${originalName.split('.').pop()}`;
    },
    
    // 删除文件
    removeFile() {
      this.fileUrl = '';
    },
    
    // 预览图片
    previewImage() {
      // el-image 组件会自动处理预览，这里可以添加额外逻辑
      // 或者手动触发预览
    },

    // 预览视频
    previewVideo() {
      this.$msgbox({
        title: '视频预览',
        message: this.$createElement('video', {
          attrs: {
            src: this.fileUrl,
            controls: true,
            autoplay: true,
            style: 'width: 100%; max-height: 600px;'
          }
        }),
        showCancelButton: false,
        confirmButtonText: '关闭',
        customClass: 'video-preview-dialog'
      });
    },

    // 预览其他文件
    previewFile() {
      window.open(this.fileUrl, '_blank');
    },
    
    // 提取文件名
    getFileNameFromUrl(url) {
      if (!url) return '';
      const parts = url.split('/');
      return parts[parts.length - 1] || '未知文件';
    },
    
    // 获取文件扩展名
    getFileExtension(url) {
      if (!url) return '';
      const fileName = this.getFileNameFromUrl(url);
      const parts = fileName.split('.');
      return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : 'FILE';
    },
    
    // 判断是否为图片文件
    isImageFile(url) {
      const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
      const ext = this.getFileExtension(url).toLowerCase();
      return imageExtensions.includes(ext);
    },
    
    // 判断是否为视频文件
    isVideoFile(url) {
      const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
      const ext = this.getFileExtension(url).toLowerCase();
      return videoExtensions.includes(ext);
    }
  }
};
</script>

<style scoped>
.upload-common-container {
  display: inline-block;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

.upload-common-container:hover {
  border-color: #409EFF;
}

.upload-trigger {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #fafafa;
}


.upload-icon {
  font-size: 36px;
  color: #8c939d;
  margin-bottom: 8px;
}

.upload-text {
  font-size: 14px;
  color: #8c939d;
  margin-bottom: 8px;
}

.upload-tip {
  font-size: 12px;
  color: #999;
}

/* 文件预览容器样式 */
.file-preview {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}

/* 图片预览样式 */
.image-preview {
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 40px;
  min-height: 40px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.uploaded-image {
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  flex-shrink: 0;
}

.image-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px; /* 减小间距 */
  opacity: 0;
  transition: opacity 0.3s;
}

.image-preview:hover .image-actions {
  opacity: 1;
}

.image-actions i {
  color: #ffffff;
  font-size: 18px; /* 减小图标尺寸 */
  cursor: pointer;
}

.image-actions i:hover {
  color: #409EFF;
}

/* 视频预览样式 */
.video-preview {
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 40px;
  min-height: 40px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-thumbnail {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-preview-element {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}

.video-play-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.video-play-overlay i {
  color: #ffffff;
  font-size: 24px;
  margin-left: 3px; /* 调整播放图标位置 */
}

.video-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
}

.video-preview:hover .video-actions {
  opacity: 1;
}

.video-preview:hover .video-play-overlay {
  opacity: 0.3;
}

.video-actions i {
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
}

.video-actions i:hover {
  color: #409EFF;
}

/* 其他文件样式 */
.other-file-preview {
  width: 100%;
  height: 100%;
  position: relative;
  min-width: 40px;
  min-height: 40px;
  background-color: #fafafa;
  border-radius: 4px;
}

.file-content {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 8px;
  box-sizing: border-box;
}

.file-icon {
  position: relative;
  margin-bottom: 8px;
  flex-shrink: 0;
}

.file-icon i {
  font-size: 32px;
  color: #409EFF;
}

.file-extension {
  position: absolute;
  bottom: -2px;
  right: -8px;
  background: #409EFF;
  color: white;
  font-size: 10px;
  font-weight: bold;
  padding: 2px 4px;
  border-radius: 3px;
  line-height: 1;
}

.file-name {
  color: #409EFF;
  text-decoration: underline;
  font-size: 12px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  text-align: center;
  flex-shrink: 0;
}

.file-actions {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  opacity: 0;
  transition: opacity 0.3s;
  border-radius: 4px;
}

.other-file-preview:hover .file-actions {
  opacity: 1;
}

.file-actions i {
  color: #ffffff;
  font-size: 18px;
  cursor: pointer;
}

.file-actions i:hover {
  color: #409EFF;
}

.file-actions i.el-icon-delete:hover {
  color: #ff4949;
}
</style>

<style>
/* 视频预览对话框样式 */
.video-preview-dialog .el-message-box {
  width: auto;
  max-width: 80vw;
}

.video-preview-dialog .el-message-box__content {
  padding: 20px;
}

.video-preview-dialog .el-message-box__message {
  margin: 0;
}
</style>